import { useState } from 'react';
import type { LeadAffiliateLink, LeadDetail } from '../api/crm';
import { affiliateFlow } from './crm-flow';
import './crm-affiliate-flow.css';

function fmtDate(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

interface Step {
  label: string;
  sub: string;
  done: boolean;
}

export function CrmAffiliateFlow({
  lead,
  affiliate,
  busy,
  onSendRegistration,
  onConvert,
  onOpenAffiliate,
  onReject,
  onReopen,
}: {
  lead: LeadDetail;
  affiliate: LeadAffiliateLink | null;
  busy: boolean;
  onSendRegistration: () => void;
  onConvert: (market: 'CZ' | 'SK' | null) => void;
  onOpenAffiliate?: (papAffiliateId: string) => void;
  onReject: () => void;
  onReopen: () => void;
}) {
  const sent = !!lead.registrationSentAt;
  const clicked = !!lead.registrationClickedAt;
  const converted = !!lead.convertedAt;
  const paired = !!affiliate?.paired;
  const rejected = lead.status === 'rejected';
  const langMarket: 'CZ' | 'SK' | null = lead.lang === 'sk' ? 'SK' : lead.lang === 'cs' ? 'CZ' : null;
  const [market, setMarket] = useState<'' | 'CZ' | 'SK'>('');

  const flow = affiliateFlow(lead, paired);
  const subs = [
    `${fmtDate(lead.createdAt)} · ${lead.confirmedAt ? 'e-mail ověřen' : 'e-mail neověřen'}`,
    sent ? 'Zájemce schválen' : 'Web a telefonát',
    clicked ? `Otevřel ${fmtDate(lead.registrationClickedAt)}` : sent ? `Odesláno ${fmtDate(lead.registrationSentAt)}` : 'Pozvánka e-mailem',
    converted ? `Získán ${fmtDate(lead.convertedAt)}` : 'Údaje a bankovní účet',
    paired ? `PaP ID ${affiliate?.papAffiliateId}` : converted ? 'Doplnit PaP ID' : 'Karta partnera',
  ];
  const steps: Step[] = flow.labels.map((label, i) => ({ label, sub: subs[i] ?? '', done: !!flow.done[i] }));
  const current = rejected ? -1 : flow.current;
  const doneCount = flow.doneCount;
  const rejectLink = (
    <button type="button" className="btn btn-link crm-aflow-link is-danger" disabled={busy} onClick={onReject}>Neschválit</button>
  );

  const openCard = affiliate && onOpenAffiliate
    ? <button type="button" className="btn btn-primary crm-aflow-btn" onClick={() => onOpenAffiliate(affiliate.papAffiliateId)}>Otevřít kartu partnera</button>
    : null;

  let title: string;
  let text: string;
  let actions: JSX.Element | null = null;
  if (rejected) {
    title = 'Zájemce neschválen';
    text = 'Lead je uzavřený jako neschválený, například spam nebo zájemce, který nevyhovuje. Když jste se spletli, vraťte ho zpět do posouzení.';
    actions = (
      <button type="button" className="btn btn-outline-secondary crm-aflow-btn is-outline" disabled={busy} onClick={onReopen}>Vrátit do posouzení</button>
    );
  } else if (!sent) {
    title = 'Posuďte zájemce';
    text = 'Prohlédněte si web zájemce a zavolejte mu. Když je vhodný, schvalte ho. Odejde mu e-mail s odkazem na registraci do affiliate platformy.';
    actions = (
      <>
        <button type="button" className="btn btn-primary crm-aflow-btn" disabled={busy || !lead.email} onClick={onSendRegistration}>
          Schválit a poslat registraci
        </button>
        {!lead.email && <span className="crm-aflow-note">Lead nemá e-mail. Doplňte ho v Údajích.</span>}
        {rejectLink}
      </>
    );
  } else if (!converted) {
    title = clicked ? 'Zájemce otevřel registraci' : 'Čekáme na registraci zájemce';
    text = 'Až zájemce vyplní registraci, zkontrolujte v affiliate platformě jeho údaje včetně bankovního účtu a schvalte ho. Potom ho tady získejte jako affiliate partnera.';
    const chosen = langMarket ?? (market || null);
    actions = (
      <>
        {!langMarket && (
          <select className="form-select crm-aflow-market" value={market} disabled={busy} onChange={(e) => setMarket(e.target.value as '' | 'CZ' | 'SK')} aria-label="Trh affiliate partnera">
            <option value="">Vyberte trh</option>
            <option value="CZ">Česko</option>
            <option value="SK">Slovensko</option>
          </select>
        )}
        <button type="button" className="btn btn-primary crm-aflow-btn" disabled={busy || !chosen} onClick={() => onConvert(chosen)}>
          Získat jako affiliate
        </button>
        <button type="button" className="btn btn-link crm-aflow-link" disabled={busy || !lead.email} onClick={onSendRegistration}>
          Poslat registraci znovu
        </button>
        {rejectLink}
      </>
    );
  } else if (!paired) {
    title = 'Doplňte PaP ID';
    text = affiliate
      ? 'Affiliate partner je založený a lead je uzavřený jako úspěšný. Na kartě partnera zadejte jeho PaP ID z affiliate platformy, aby se napojily výkony a vyúčtování.'
      : 'Lead je uzavřený jako úspěšný, ale kartu affiliate partnera se nepodařilo najít.';
    actions = openCard;
  } else {
    title = 'Hotovo';
    text = `Zájemce je affiliate partnerem a je spárovaný s affiliate platformou (PaP ID ${affiliate?.papAffiliateId}).`;
    actions = openCard;
  }

  return (
    <section className={`crm-aflow${rejected ? ' is-rejected' : ''}`} aria-label="Postup affiliate zájemce">
      <header className="crm-aflow-head">
        <span className="crm-detail-eyebrow">Postup affiliate zájemce</span>
        <span className="crm-aflow-count">{rejected ? 'Neschváleno' : `${doneCount} z ${steps.length}`}</span>
      </header>
      <ol className="crm-aflow-steps">
        {steps.map((s, i) => (
          <li key={s.label} className={`crm-aflow-step ${s.done ? 'is-done' : i === current ? 'is-current' : ''}`}>
            <span className="crm-aflow-dot" aria-hidden="true">{s.done ? '✓' : i + 1}</span>
            <span className="crm-aflow-label">{s.label}</span>
            <span className="crm-aflow-sub">{s.sub}</span>
          </li>
        ))}
      </ol>
      <div className="crm-aflow-action">
        <div className="crm-aflow-copy">
          <strong>{title}</strong>
          <p>{text}</p>
        </div>
        {actions && <div className="crm-aflow-buttons">{actions}</div>}
      </div>
    </section>
  );
}
